<template>
  <Dialog
    v-model="isOpen"
    v-bind="filteredAttrs"
    max-width="800px"
    :persistent="saveInProgress || reportIsLoading"
    :content-class="'sample-reception-dialog'"
    :disabled="reportIsLoading || dataLoading || orderStatusId === null"
    @save="handleSave"
    @cancel="$emit('cancel')"
  >
    <template
      v-if="dataLoading"
      #content
    >
      <div class="text-center">
        <v-progress-circular
          indeterminate
          color="primary"
        />
      </div>
    </template>

    <template
      v-else-if="!dataLoading && localOrder"
      #content
    >
      <InputText
        v-model="localOrder.cegatId"
        :label="$t('CeGaT ID')"
        :disabled="saveInProgress || reportIsLoading"
      />
      <InputText
        v-model="localOrder.labOrderId"
        :label="$t('Lab Order ID')"
        :disabled="saveInProgress || reportIsLoading"
      />
      <InputText
        v-model="localOrder.comment"
        :label="$t('Comments (internal)')"
        multiline
        :disabled="saveInProgress || reportIsLoading"
      />

      <OrderStatus
        :statuses="statuses"
        :order="localOrder"
        :save-in-progress="saveInProgress"
        :disabled="reportIsLoading"
      />

      <div class="download-order-summary">
        <OrderSummaryDownload />
      </div>

      <div
        v-if="!reports || reports.length === 0"
        class="d-flex justify-center"
      >
        {{ $t("No reports found") }}
      </div>

      <OrderReportDownload
        v-else
        :loaded-value="loadedValue"
        :reports="reports"
        report-type="supportingFiles"
        @isLoading="loading"
      />
    </template>
  </Dialog>
</template>

<script lang="ts" setup>
import { ref, watch, computed, toRaw, useAttrs } from "vue";
import Dialog from "@/components/dialogs/Dialog.vue";
import InputText from "@/components/input-components/InputText.vue";
import OrderStatus from "@/components/orders/OrderStatus.vue";
import OrderReportDownload from "@/components/orders/OrderReportDownload.vue";
import OrderSummaryDownload from "@/components/orders/OrderSummaryDownload.vue";
import { useOrdersStore } from '@/stores/orders';

defineOptions({
  name: "SampleReceptionDialog",
  inheritAttrs: false
});

const attrs = useAttrs();

const props = defineProps({
  statuses: { type: Array, default: () => [] },
  dataLoading: { type: Boolean, required: true },
  order: { type: Object, default: null },
  reports: { type: Array, default: () => [] },
  loadedValue: { type: Number, default: null },
  modelValue: { type: Boolean, required: true },
});

const ordersStore = useOrdersStore();

const emit = defineEmits(["update:modelValue", "update:order", "save", "cancel"]);

const reportIsLoading = ref(false);

// use reactive copy that always stays in sync
const localOrder = ref({ ...(props.order ?? {}) });

const filteredAttrs = computed(() => {
  const { modelValue, 'onUpdate:modelValue': _, ...rest } = attrs;
  return rest;
});

const isOpen = computed({
  get: () => props.modelValue,
  set: (val: boolean) => emit("update:modelValue", val),
});

watch(
  () => props.order,
  (val: any) => {
    if (val) {
      localOrder.value = { ...val };
    }
  },
  { deep: true, immediate: true }
);

watch(
  localOrder,
  (val: any) => emit("update:order", { ...val }),
  { deep: true }
);

const saveInProgress = computed(
  () => (props as any)["save-in-progress"] ?? false
);

const orderStatusId = computed(() => localOrder.value?.statusId ?? null);

function loading(isLoading: boolean) {
  reportIsLoading.value = isLoading;
}

function handleSave() {
  emit("update:order", { ...toRaw(localOrder.value) });
  ordersStore.order = { ...toRaw(localOrder.value) };
  emit("save", { ...toRaw(localOrder.value) });
}
</script>

<style lang="scss">
.sample-reception-dialog {
  .download-order-summary {
    display: flex;
    justify-content: center;
    padding-bottom: 1rem;
  }
}
</style>
